// Phase 14e-a — /admin/policies single-page with two stacked sections.
//
// Per 14e-a-6 lock: stacked sections (not tabs, not sub-routes) until
// Phase 15 lands a 3rd policy section (compliance) OR operator feedback
// flags scrolling friction. Both policy families consume the same
// <SupersedableResourcePanel> shape — different schemas, identical UX.

import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { SupersedableResourcePanel } from '../components/SupersedableResourcePanel';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/policies')({
  beforeLoad: () => adminBeforeLoad('/policies'),
  component: PoliciesPage,
});

// ─── Cancellation policy ───────────────────────────────────────────────────

// Values must match the backend BuyerType enum (`HOMECARE_CLIENT`, not
// `HOMECARE` — the old value 400'd every homecare tier create).
const BUYER_TYPES = [
  { value: 'FACILITY', label: 'Facility (LTC)' },
  { value: 'HOMECARE_CLIENT', label: 'Homecare client' },
] as const;

const buyerTypeLabel = (v: string): string =>
  BUYER_TYPES.find((b) => b.value === v)?.label ?? v;

const CANCELLATION_CREATE_SCHEMA = z.object({
  buyerType: z.enum(['FACILITY', 'HOMECARE_CLIENT']),
  hoursBeforeThreshold: z.number().int().positive().max(168),
  chargePct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter 0-100'),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const CANCELLATION_SUPERSEDE_SCHEMA = z.object({
  newChargePct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const DEPRECATE_SCHEMA = z.object({
  effectiveTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

// Edit (unused rows) — value % only; identity + dates frozen.
const CANCELLATION_EDIT_SCHEMA = z.object({
  chargePct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter 0-100'),
});

interface CancellationPolicyRow {
  id: string;
  buyerType: string;
  hoursBeforeThreshold: number;
  chargePct: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  inUse: boolean;
}

// ─── Caregiver-guarantee policy ─────────────────────────────────────────────

const GUARANTEE_CREATE_SCHEMA = z.object({
  buyerType: z.enum(['FACILITY', 'HOMECARE_CLIENT']),
  hoursBeforeThreshold: z.number().int().positive().max(168),
  payPct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter 0-100'),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const GUARANTEE_SUPERSEDE_SCHEMA = z.object({
  newPayPct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(10).max(500),
  overrideLeadTime: z.boolean().optional(),
  overrideReason: z.string().optional(),
});

const GUARANTEE_EDIT_SCHEMA = z.object({
  payPct: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter 0-100'),
});

interface GuaranteePolicyRow {
  id: string;
  buyerType: string;
  hoursBeforeThreshold: number;
  payPct: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  inUse: boolean;
}

// ─── Page ──────────────────────────────────────────────────────────────────

function PoliciesPage() {
  return (
    <AdminLayout>
      <div className="space-y-8">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Policy management</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Cancellation and payout-guarantee policies. Changes take effect on a
            future date and follow the 14-day lead-time rule.
          </p>
          <nav className="mt-3 flex gap-4 text-sm text-ink-muted">
            <a href="#cancellation" className="hover:text-forest">
              Cancellation policies
            </a>
            <span>•</span>
            <a href="#caregiver-guarantee" className="hover:text-forest">
              Caregiver-guarantee policies
            </a>
          </nav>
        </header>

        <section id="cancellation" className="scroll-mt-24">
          <SupersedableResourcePanel<CancellationPolicyRow>
            labels={{
              panelTitle: 'Cancellation policies (Billing)',
              singular: 'cancellation policy',
              plural: 'cancellation policies',
            }}
            columns={[
              {
                key: 'buyerType',
                header: 'Buyer type',
                width: 'w-40',
                render: (r) => buyerTypeLabel(r.buyerType),
              },
              { key: 'hoursBeforeThreshold', header: 'Hours before', width: 'w-28' },
              {
                key: 'chargePct',
                header: 'Charge %',
                width: 'w-28',
                render: (r) => `${r.chargePct}%`,
              },
              { key: 'effectiveFrom', header: 'From', width: 'w-28' },
              {
                key: 'effectiveTo',
                header: 'To',
                width: 'w-28',
                render: (r) => r.effectiveTo ?? '—',
              },
            ]}
            endpoints={{
              list: async () => {
                 
                const r = (await unwrap(
                  api.GET('/api/v1/admin/billing/cancellation-policies' as never, {
                    params: { query: { includeHistorical: 'true' } },
                  } as never),
                )) as { items: CancellationPolicyRow[] };
                return r;
              },
               
              create: (body) => unwrap(api.POST('/api/v1/admin/billing/cancellation-policies' as never, { body } as never)),
              supersede: (id, body) =>
                 
                unwrap(
                  api.POST('/api/v1/admin/billing/cancellation-policies/{id}/supersede' as never, {
                    params: { path: { id } },
                    body,
                  } as never),
                ),
              deprecate: (id, body) =>

                unwrap(
                  api.POST('/api/v1/admin/billing/cancellation-policies/{id}/deprecate' as never, {
                    params: { path: { id } },
                    body,
                  } as never),
                ),
              // Unused rows only (panel gates on inUse): in-place edit + delete.
              edit: (id, body) =>
                unwrap(
                  api.PATCH('/api/v1/admin/billing/cancellation-policies/{id}' as never, {
                    params: { path: { id } },
                    body,
                  } as never),
                ),
              delete: (id) =>
                unwrap(
                  api.DELETE('/api/v1/admin/billing/cancellation-policies/{id}' as never, {
                    params: { path: { id } },
                  } as never),
                ),
            }}
            form={{
              createSchema: CANCELLATION_CREATE_SCHEMA,
              createFields: [
                { kind: 'select', name: 'buyerType', label: 'Buyer type', options: BUYER_TYPES },
                {
                  kind: 'number',
                  name: 'hoursBeforeThreshold',
                  label: 'Hours before shift',
                  min: 1,
                  max: 168,
                  help: 'Below this threshold the charge applies. Max 168 (1 week).',
                },
                { kind: 'percent', name: 'chargePct', label: 'Charge percentage' },
              ],
              supersedeSchema: CANCELLATION_SUPERSEDE_SCHEMA,
              supersedeFields: [{ kind: 'percent', name: 'newChargePct', label: 'New charge percentage' }],
              supersedeDefaultValues: (e) => ({ newChargePct: e.chargePct }),
              deprecateSchema: DEPRECATE_SCHEMA,
              editSchema: CANCELLATION_EDIT_SCHEMA,
              editFields: [{ kind: 'percent', name: 'chargePct', label: 'Charge percentage' }],
              editDefaultValues: (e) => ({ chargePct: e.chargePct }),
            }}
            query={{ queryKey: ['admin', 'billing', 'cancellation-policies'] }}
          />
        </section>

        <section id="caregiver-guarantee" className="scroll-mt-24">
          <SupersedableResourcePanel<GuaranteePolicyRow>
            labels={{
              panelTitle: 'Caregiver-guarantee policies (Payouts)',
              singular: 'caregiver-guarantee policy',
              plural: 'caregiver-guarantee policies',
            }}
            columns={[
              {
                key: 'buyerType',
                header: 'Cancelled by',
                width: 'w-40',
                render: (r) => buyerTypeLabel(r.buyerType),
              },
              { key: 'hoursBeforeThreshold', header: 'Hours before', width: 'w-28' },
              {
                key: 'payPct',
                header: 'Pay %',
                width: 'w-28',
                render: (r) => `${r.payPct}%`,
              },
              { key: 'effectiveFrom', header: 'From', width: 'w-28' },
              {
                key: 'effectiveTo',
                header: 'To',
                width: 'w-28',
                render: (r) => r.effectiveTo ?? '—',
              },
            ]}
            endpoints={{
              list: async () => {
                 
                const r = (await unwrap(
                  api.GET('/api/v1/admin/billing/caregiver-guarantee-policies' as never, {
                    params: { query: { includeHistorical: 'true' } },
                  } as never),
                )) as { items: GuaranteePolicyRow[] };
                return r;
              },
               
              create: (body) => unwrap(api.POST('/api/v1/admin/billing/caregiver-guarantee-policies' as never, { body } as never)),
              supersede: (id, body) =>
                 
                unwrap(
                  api.POST('/api/v1/admin/billing/caregiver-guarantee-policies/{id}/supersede' as never, {
                    params: { path: { id } },
                    body,
                  } as never),
                ),
              deprecate: (id, body) =>

                unwrap(
                  api.POST('/api/v1/admin/billing/caregiver-guarantee-policies/{id}/deprecate' as never, {
                    params: { path: { id } },
                    body,
                  } as never),
                ),
              // Unused rows only (panel gates on inUse): in-place edit + delete.
              edit: (id, body) =>
                unwrap(
                  api.PATCH('/api/v1/admin/billing/caregiver-guarantee-policies/{id}' as never, {
                    params: { path: { id } },
                    body,
                  } as never),
                ),
              delete: (id) =>
                unwrap(
                  api.DELETE('/api/v1/admin/billing/caregiver-guarantee-policies/{id}' as never, {
                    params: { path: { id } },
                  } as never),
                ),
            }}
            form={{
              createSchema: GUARANTEE_CREATE_SCHEMA,
              createFields: [
                { kind: 'select', name: 'buyerType', label: 'Cancelled by', options: BUYER_TYPES },
                {
                  kind: 'number',
                  name: 'hoursBeforeThreshold',
                  label: 'Hours before shift',
                  min: 1,
                  max: 168,
                },
                {
                  kind: 'percent',
                  name: 'payPct',
                  label: 'Guarantee pay percentage',
                  help: 'Percentage of the care professional\'s shift pay, paid when this buyer type cancels late.',
                },
              ],
              supersedeSchema: GUARANTEE_SUPERSEDE_SCHEMA,
              supersedeFields: [{ kind: 'percent', name: 'newPayPct', label: 'New pay percentage' }],
              supersedeDefaultValues: (e) => ({ newPayPct: e.payPct }),
              deprecateSchema: DEPRECATE_SCHEMA,
              editSchema: GUARANTEE_EDIT_SCHEMA,
              editFields: [{ kind: 'percent', name: 'payPct', label: 'Guarantee pay percentage' }],
              editDefaultValues: (e) => ({ payPct: e.payPct }),
            }}
            query={{ queryKey: ['admin', 'billing', 'caregiver-guarantee-policies'] }}
          />
        </section>
      </div>
    </AdminLayout>
  );
}
